import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Heart, Trophy, Crown, Medal, Star, Users, DollarSign, RefreshCw, Loader2 } from 'lucide-react';
import { API } from '../config/api';
import { DonateButton } from './DonateModal';

const BADGE_STYLES = {
  platinum: {
    bg: 'bg-gradient-to-r from-slate-300 to-slate-100',
    text: 'text-slate-900',
    icon: Crown,
    glow: 'shadow-slate-400/30'
  },
  gold: {
    bg: 'bg-gradient-to-r from-yellow-500 to-amber-400',
    text: 'text-yellow-900',
    icon: Trophy,
    glow: 'shadow-yellow-500/30'
  },
  silver: {
    bg: 'bg-gradient-to-r from-gray-400 to-gray-300',
    text: 'text-gray-800',
    icon: Medal,
    glow: 'shadow-gray-400/30'
  },
  bronze: {
    bg: 'bg-gradient-to-r from-orange-600 to-orange-400',
    text: 'text-orange-100',
    icon: Medal,
    glow: 'shadow-orange-500/30'
  },
  supporter: {
    bg: 'bg-gradient-to-r from-pink-500 to-rose-400',
    text: 'text-foreground',
    icon: Heart,
    glow: 'shadow-pink-500/30'
  }
};

const RANK_STYLES = {
  1: 'text-yellow-400 font-bold text-2xl',
  2: 'text-gray-300 font-bold text-xl',
  3: 'text-orange-400 font-bold text-xl',
};

export function TopSupporters({ showTitle = true, limit = 5, compact = false }) {
  const [supporters, setSupporters] = useState([]);
  const [totals, setTotals] = useState({ total_raised: 0, total_donations: 0, unique_donors: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSupporters();
  }, [limit]);

  const fetchSupporters = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/donations/top-supporters?limit=${limit}`);
      if (response.ok) {
        const data = await response.json();
        setSupporters(data.supporters || []);
        setTotals(data.totals || { total_raised: 0, total_donations: 0, unique_donors: 0 });
      }
    } catch (err) {
      console.error('[TopSupporters] Error:', err);
      setError('Failed to load supporters');
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <Card className="p-6 glass-card border-border">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  if (compact) {
    return (
      <div className="space-y-3" data-testid="top-supporters-compact">
        {supporters.length > 0 ? (
          supporters.slice(0, 3).map((supporter, idx) => {
            const style = BADGE_STYLES[supporter.badge] || BADGE_STYLES.supporter;
            const BadgeIcon = style.icon;
            
            return (
              <div 
                key={idx}
                className="flex items-center gap-3 p-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                <span className={RANK_STYLES[supporter.rank] || 'text-muted-foreground'}>
                  #{supporter.rank}
                </span>
                <div className={`w-8 h-8 rounded-full ${style.bg} flex items-center justify-center shadow-lg ${style.glow}`}>
                  <BadgeIcon className={`w-4 h-4 ${style.text}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{supporter.donor_name}</p>
                </div>
                <span className="font-bold text-green-500">{formatAmount(supporter.total_donated)}</span>
              </div>
            );
          })
        ) : (
          <p className="text-center text-muted-foreground py-4">Be the first supporter!</p>
        )}
      </div>
    );
  }

  return (
    <Card className="p-6 glass-card border-border" data-testid="top-supporters">
      {showTitle && (
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Top Supporters
          </h3>
          <Button variant="ghost" size="sm" onClick={fetchSupporters}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Totals Banner */}
      <div className="grid grid-cols-3 gap-4 mb-6 p-4 rounded-lg bg-gradient-to-r from-primary/20 to-secondary/20 border border-border">
        <div className="text-center">
          <DollarSign className="w-5 h-5 mx-auto mb-1 text-green-500" />
          <p className="text-2xl font-bold text-green-500">{formatAmount(totals.total_raised)}</p>
          <p className="text-xs text-muted-foreground">Total Raised</p>
        </div>
        <div className="text-center">
          <Heart className="w-5 h-5 mx-auto mb-1 text-red-500" />
          <p className="text-2xl font-bold">{totals.total_donations}</p>
          <p className="text-xs text-muted-foreground">Donations</p>
        </div>
        <div className="text-center">
          <Users className="w-5 h-5 mx-auto mb-1 text-blue-500" />
          <p className="text-2xl font-bold">{totals.unique_donors}</p>
          <p className="text-xs text-muted-foreground">Supporters</p>
        </div>
      </div>

      {/* Supporters List */}
      <div className="space-y-3">
        {supporters.length > 0 ? (
          supporters.map((supporter, idx) => {
            const style = BADGE_STYLES[supporter.badge] || BADGE_STYLES.supporter;
            const BadgeIcon = style.icon;
            
            return (
              <div 
                key={idx}
                className={`flex items-center gap-4 p-3 rounded-lg transition-all ${
                  supporter.rank <= 3 
                    ? 'bg-gradient-to-r from-white/10 to-transparent border border-border' 
                    : 'bg-muted/30 hover:bg-muted/50'
                }`}
                data-testid={`supporter-${supporter.rank}`}
              >
                {/* Rank */}
                <div className="w-8 text-center">
                  {supporter.rank === 1 ? (
                    <Crown className="w-6 h-6 text-yellow-400 mx-auto" />
                  ) : supporter.rank === 2 ? (
                    <span className="text-xl font-bold text-gray-300">2</span>
                  ) : supporter.rank === 3 ? (
                    <span className="text-xl font-bold text-orange-400">3</span>
                  ) : (
                    <span className="text-lg text-muted-foreground">#{supporter.rank}</span>
                  )}
                </div>

                {/* Badge Icon */}
                <div className={`w-10 h-10 rounded-full ${style.bg} flex items-center justify-center shadow-lg ${style.glow}`}>
                  <BadgeIcon className={`w-5 h-5 ${style.text}`} />
                </div>

                {/* Name & Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold truncate">{supporter.donor_name}</p>
                    {supporter.is_recurring_supporter && (
                      <Badge variant="outline" className="text-xs border-purple-500 text-purple-400">
                        Monthly
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {supporter.donation_count} donation{supporter.donation_count !== 1 ? 's' : ''}
                  </p>
                </div>

                {/* Amount */}
                <div className="text-right">
                  <p className="font-bold text-lg text-green-500">{formatAmount(supporter.total_donated)}</p>
                  <Badge className={`${style.bg} ${style.text} text-xs`}>
                    {supporter.badge_label}
                  </Badge>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8">
            <Heart className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
            <p className="text-muted-foreground mb-4">No supporters yet. Be the first!</p>
            <DonateButton variant="default" />
          </div>
        )}
      </div>

      {/* CTA */}
      {supporters.length > 0 && (
        <div className="mt-6 pt-4 border-t border-border text-center">
          <p className="text-sm text-muted-foreground mb-3">
            Join the leaderboard and support DMV Soundstage!
          </p>
          <DonateButton variant="default" className="bg-gradient-to-r from-primary to-secondary" />
        </div>
      )}
    </Card>
  );
}

export default TopSupporters;
